/**
 * ## ServerPageProvider.tsx ##
 * This file contains ServerPageProvider component
 * @packageDocumentation
 */

import React from 'react';

import { useSelector, shallowEqual } from 'react-redux';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';

import once from 'lodash/once';

import {
	ItemProvider,
	useItemProviderContext,
} from '@common/react/components/Core/ItemProvider/ItemProvider';
import Loader from '@common/react/components/Core/LoaderProvider/Loader';
import SimpleLoader from '@common/react/components/UI/Loaders/BounceLoader';
import PageNotFound from '@common/react/components/UI/PageNotFound/PageNotFound';
import NotFoundComponent from '@common/react/components/Core/NotFoundPageProvider/NotFoundComponent';
import { Lang } from '@common/typescript/objects/Lang';
import { useRequestProviderContext } from '@common/react/components/RequestProvider/RequestProvider';
import { Page } from '@common/typescript/objects/Page';

export type HandlerBackDelegate = (path?: string) => void;

export interface ServerPageProviderContext {
	state: {
		page: any,
		getFromContext?: boolean;
		error?: string;
		loading?: boolean;
	};
}

export const createServerPageProviderContext = once(() => React.createContext({} as ServerPageProviderContext));

export const useServerPageProviderContext: () => ServerPageProviderContext = () =>
	React.useContext(createServerPageProviderContext());

export interface SppData extends Page {
	item?: any;
	items?: any;
	filterProps?: any;
	message?: any;
}

interface InnerOrChildrenOptions {
	handlerBack: HandlerBackDelegate;
}

/**
 * This is the description of the interface
 *
 * @interface ServerPageProviderProps
 */
export interface ServerPageProviderProps<T extends SppData = SppData, InnerPage extends SppData = SppData> {
	children?: React.ReactNode | ((initPage: T & { innerPage?: InnerPage }, options: InnerOrChildrenOptions) => React.ReactNode);
	/**
	 * render content
	 * @param initPage - serverPage data. May not be defined
	 * @return page content
	 */
	inner?: (initPage: T & { innerPage?: InnerPage }, options: InnerOrChildrenOptions) => React.ReactNode;
	/**
	 * pathname where we want to display the page. Used to avoid using data from another page
	 */
	path?: string;
	/**
	 * a function that determines what state to take from redux
	 * @param state - redux state
	 * @return global page data
	 */
	getState?: (state) => any;
	/**
	 * custom loader, by default <Loader defaultLoader={<SimpleLoader />} type="pageLoader" />
	 */
	loader?: React.ReactNode;
	/**
	 * custom content for not found page. by default <NotFoundComponent defaultNotFoundComponent={<PageNotFound />} />
	 */
	notFoundComponent?: React.ReactNode;
	/**
	 * a function to take the value of an element from the page data
	 * @param value - global page data
	 * @return page date
	 */
	transformToItem?: (value) => any;
	/**
	 * default initPage. Used when we have page data but the data from Redux is empty.
	 */
	page?: T & { innerPage?: InnerPage };
	/**
	 * true if we don't have to use a helmet
	 */
	withoutHelmet?: boolean;
	/**
	 * By default used title from page langs
	 */
	customTitle?: string;
	/**
	 * returns which language to use
	 */
	language?: Lang;
	/**
	 * sends what title will be if there is no customTitle and data from langs
	 */
	defaultTitle?: string;
	/**
	 * used if an error occurred while loading data
	 * @param error - error message
	 */
	onRequestError?: (error: string) => void;
	/**
	 * function to separate when you don't need to use data from spp.
	 * By default it ignore serverPage when id from url not define or id < 0
	 * @param error - error message
	 */
	getIgnoreServerPage?: (id: number | undefined | string, initPage: T & { innerPage?: InnerPage }) => boolean;
	/**
	 * determines whether to load data again when changing pathname
	 */
	reloadByPathChange?: boolean;
	/**
	 * additional params for load request
	 */
	additionalParams?: any;
	/**
	 * determines whether the child spp should take values from the context of the current
	 */
	getInnerPageFromContext?: boolean;
	/**
	 * time to live (ms) for cached response at RequestProvider if cache is available
	 */
	ttl?: number;
	/**
	 * default back path for handleBack
	 */
	backPath?: string;
}

interface ServerPageProviderInnerProps<T extends SppData = SppData, InnerPage extends SppData = SppData> extends
	Omit<ServerPageProviderProps<T, InnerPage>, 'ttl' | 'getInnerPageFromContext' | 'additionalParams' | 'transformToItem' | 'path' | 'getState'> {
	pathname: string;
	skipInitLoad: boolean;
}

const defaultGetState = (state) => state.serverPage;

const defaultHandleBack = (navigate, location, backPath?: string) => {
	if (location.state?.prevPath) {
		navigate(location.state.prevPath);
	} else {
		navigate(backPath ?? '/');
	}
};

const ServerPageProviderInner = <T extends SppData = SppData, InnerPage extends SppData = SppData>({
	inner,
	loader,
	notFoundComponent,
	pathname,
	skipInitLoad,
	withoutHelmet,
	customTitle,
	language,
	defaultTitle,
	reloadByPathChange,
	children,
	backPath,
}: ServerPageProviderInnerProps<T, InnerPage>) => {
	const {
		state: {
			item, loading, error, itemLoading,
		},
		actions: {
			load,
		},
	} = useItemProviderContext<any>();
	const location = useLocation();
	const navigate = useNavigate();

	const [skipLoad, setSkipLoad] = React.useState(skipInitLoad);
	const [prevPath, setPrevPath] = React.useState(pathname);

	React.useEffect(() => {
		if ((error || (reloadByPathChange && prevPath !== pathname)) && !skipLoad) {
			load(undefined, { preventHandleNotFound: true });
			setPrevPath(pathname);
		}
		setSkipLoad(false);
	}, [pathname, reloadByPathChange]);

	const info = React.useMemo(() => item?.langs?.find((lang) => lang.language === language), [item, language]);
	const title = customTitle || info?.title || defaultTitle;
	const resItem = error ? { ...item, error: item?.error || error } : item;
	const handlerBack = (path = backPath) => {
		defaultHandleBack(navigate, location, path);
	};
	const options = { handlerBack };

	return (<>
		{!withoutHelmet && <Helmet>
			{title && <title>{title}</title>}
		</Helmet>}
		{(error === 'Page not found')
			? notFoundComponent
			: (loading || !item || itemLoading) && !error
				? loader
				: (
					typeof children !== 'undefined'
						? typeof children === 'function' ? children(resItem, options) : children
						: inner?.(resItem, options)
				)
		}
	</>);
};

const ServerPageProvider = <T extends SppData, InnerPage extends SppData = SppData>(props: ServerPageProviderProps<T, InnerPage>) => {
	const SppContext = createServerPageProviderContext();
	const sppContext = useServerPageProviderContext();
	const defaultTransformToItem = sppContext?.state ? (value) => value?.innerPage : (value) => value;
	const location = useLocation();
	const navigate = useNavigate();
	const { id } = useParams<{id?: string}>();
	const {
		loader = <Loader defaultLoader={<SimpleLoader />} type="pageLoader" />,
		notFoundComponent = <NotFoundComponent defaultNotFoundComponent={<PageNotFound />} />,
		getState = defaultGetState,
		transformToItem = defaultTransformToItem,
		language = Lang.En,
		path,
		page,
		inner,
		reloadByPathChange,
		withoutHelmet,
		customTitle,
		children,
		backPath,
	} = props;
	const serverPage = useSelector(getState, shallowEqual);
	const requestContext = useRequestProviderContext();

	const [initPage, setInitPage] = React.useState<any>(() => {
		const cacheData = requestContext?.actions?.getFromCache(
			'pageLoader',
			{ path: `${location.pathname}${location.search}`, ...props.additionalParams, id: 0 },
		);
		if (cacheData) return cacheData;

		const value = page
			|| (sppContext?.state?.page && (serverPage?.page || sppContext?.state?.getFromContext)
				? sppContext?.state?.page
				: (serverPage?.path === path || path === undefined) && serverPage?.page
					? { ...serverPage?.page } : undefined);
		return value ? JSON.parse(JSON.stringify({ ...value })) : value;
	});
	const error = React.useMemo(() =>
		(serverPage.page !== null && (serverPage.page?.id === 0 || !serverPage.path) ? 'Page not found' : ''), [location.pathname]);

	const ignoreServerPage = props.getIgnoreServerPage ? props.getIgnoreServerPage(id, initPage) : id && !isNaN(+id) && +id < 0;

	const item = ignoreServerPage ? {} : transformToItem(initPage);

	React.useEffect(() => {
		if (sppContext?.state && sppContext?.state?.page && sppContext?.state?.getFromContext) {
			setInitPage(sppContext?.state?.page);
		}
	}, [sppContext?.state?.page]);

	React.useEffect(() => {
		if (serverPage.page) {
			serverPage.page = null;
		}
		if (!error && !ignoreServerPage && requestContext?.actions?.updateCache && item) {
			requestContext.actions.updateCache(
				'pageLoader',
				{ path: `${location.pathname}${location.search}`, ...props.additionalParams, id: 0 },
				item,
				props.ttl,
			);
		}
	}, []);

	if (sppContext?.state && !sppContext?.state?.page && sppContext?.state?.getFromContext) {
		return <>{loader}</>;
	}

	return (
		<ItemProvider
			ttl={props.ttl}
			item={error || ignoreServerPage ? {} : item}
			loadRequest="pageLoader"
			additionalParams={{ path: `${location.pathname}${location.search}`, ...props.additionalParams }}
			id={0}
			showLoader={false}
			error={ignoreServerPage ? undefined : error}
			onRequestError={props.onRequestError}
			skipInitLoad={!!item || !!error}
			onLoad={(response) => !initPage && setInitPage(response)}
			preventHandleNotFound
		>
			{({ state: { itemLoading } }) => <SppContext.Provider
				value={{
					state: {
						page: item, getFromContext: props.getInnerPageFromContext, error, loading: itemLoading,
					},
				}}
			>
				<ServerPageProviderInner<T, InnerPage>
					reloadByPathChange={reloadByPathChange}
					customTitle={customTitle}
					language={language}
					withoutHelmet={withoutHelmet ?? !!sppContext.state}
					defaultTitle={props.defaultTitle}
					pathname={location.pathname}
					loader={loader}
					notFoundComponent={notFoundComponent}
					inner={inner}
					skipInitLoad={ignoreServerPage || !!item || !!error}
					backPath={backPath}
				>
					{children}
				</ServerPageProviderInner>
			</SppContext.Provider>}
		</ItemProvider>
	);
};

export default ServerPageProvider;
